'use client';

import { Box, BoxProps, styled } from '@mui/material';
import { CSSProperties, forwardRef } from 'react';
import { useAppLayout } from '../AppLayoutProvider';
import { defaultSidebarWidth } from '../AppSidebar/AppSidebar';

export interface ScrollableBodyLayoutProps {
  /**
   * Page content
   */
  children?: React.ReactNode;
  /**
   * App bar with 'position: sticky'
   */
  appBar: React.ReactNode;
  /**
   * App sidebar (`<AppSidebar>`).
   *
   * Used in apps where the main navigation is in the sidebar instead of the app bar.
   *
   * If you're using a persistent sidebar, enable the feature in app layout provider:
   * ```tsx
   * <AppLayoutProvider features={['persistentSidebar']}>
   * ```
   *
   * The sidebar will become persistent on desktop and swipeable and togglable on mobile.
   *
   * To manually toggle the sidebar on desktop, use the `useAppLayout` hook.
   *
   * (!) Responsive apps with a sidebar should not have any navigation items in the app
   * bar. This would cause extra complexity on mobile, where the app bar content and the
   * sidebar content would have to be merged into a togglable mobile drawer.
   *
   * @deprecated Use `sidebars={[<AppSidebar>…</AppSidebar>]}` instead. Will be removed in a future release.
   */
  sidebar?: React.ReactNode;
  /**
   * Sidebar width (CSS value or px)
   * @deprecated Use `sidebars` instead.
   */
  sidebarWidth?: number | string;
  /**
   * Left and/or right sidebars as an array. Use `<AppSidebar anchor="right">` for the
   * right sidebar.
   *
   * ```tsx
   * sidebars={[
   *   <AppSidebar>…</AppSidebar>,
   *   <AppSidebar anchor="right">…</AppSidebar>,
   * ]}
   * ```
   *
   * Each sidebar manages its own side via its `anchor` prop.
   * The `sidebar` prop still works and is additive with this array.
   */
  sidebars?: React.ReactNode[];
  /**
   * Disable horizontal padding for the main content. This allows the main content to take
   * up the full width of the screen.
   */
  disableGutter?: boolean;
  /**
   * Additional props for the root and content containers
   */
  slotProps?: {
    root?: Partial<BoxProps>;
    content?: Partial<BoxProps>;
  };
}

const LayoutRoot = styled(Box)({
  // Required to stay auto for content to be able to stretch to remaining height
  // (body height minus sticky elements)
  height: 'auto',
  // Splash pages like the PageSpinner need to be able to stretch to full height
  minHeight: '100vh',
  // On iOS, don't stretch over the bottom toolbar
  '@supports(height: 100dvh)': {
    minHeight: '100dvh',
  },
  display: 'flex',
  flexDirection: 'column',
});

const MainContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'disableGutter' && prop !== 'isSidebarOpen' && prop !== 'isRightSidebarOpen',
})<{
  disableGutter?: boolean;
  isSidebarOpen: boolean;
  isRightSidebarOpen: boolean;
}>(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  marginLeft: 'var(--app-sidebar-width)',
  marginRight: 'var(--app-right-sidebar-width)',

  variants: [
    {
      props: (props) => !props.disableGutter,
      style: { ...theme.mixins.gutters },
    },
    {
      props: { isSidebarOpen: true },
      style: {
        transition: theme.transitions.create(['margin-left', 'margin-right'], {
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    {
      props: { isSidebarOpen: false },
      style: {
        transition: theme.transitions.create(['margin-left', 'margin-right'], {
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
    {
      props: { isRightSidebarOpen: true },
      style: {
        transition: theme.transitions.create(['margin-left', 'margin-right'], {
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    {
      props: { isRightSidebarOpen: false },
      style: {
        transition: theme.transitions.create(['margin-left', 'margin-right'], {
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

/**
 * Layout with a sticky header and vertically scrollable body. Suitable for articles or
 * pages with long content where page anchors might appear.
 *
 * If you need to customize this layout, copy-paste the implementation into your
 * application codebase and tweak it as needed.
 */
export const ScrollableBodyLayout = forwardRef<HTMLElement, ScrollableBodyLayoutProps>(function ScrollableBodyLayout(
  { children, appBar, sidebar, sidebarWidth = defaultSidebarWidth, sidebars, disableGutter, slotProps },
  ref,
) {
  const { isSidebarOpen, sidebarVariant, isRightSidebarOpen, rightSidebarVariant } = useAppLayout();

  const currentSidebarWidth =
    isSidebarOpen && sidebarVariant === 'persistent'
      ? typeof sidebarWidth === 'number'
        ? `${sidebarWidth}px`
        : sidebarWidth
      : '0';

  const currentRightSidebarWidth =
    isRightSidebarOpen && rightSidebarVariant === 'persistent' ? `${defaultSidebarWidth}px` : '0';

  return (
    <LayoutRoot
      ref={ref}
      style={
        {
          '--app-sidebar-width': currentSidebarWidth,
          '--app-right-sidebar-width': currentRightSidebarWidth,
        } as unknown as CSSProperties
      }
      {...slotProps?.root}
    >
      {appBar}

      {sidebar}

      {sidebars}

      <MainContent
        component="main"
        isSidebarOpen={isSidebarOpen}
        isRightSidebarOpen={!!isRightSidebarOpen}
        disableGutter={disableGutter}
        {...slotProps?.content}
      >
        {children}
      </MainContent>
    </LayoutRoot>
  );
});
